export interface RadioOption {
  /** Stable identifier, also used as the i18n sub-key by callers. */
  key: string;
  /** Already-translated label to display. */
  label: string;
}

interface RadioOptionListProps {
  options: readonly RadioOption[];
  /** Currently selected option key, or null when nothing is chosen yet. */
  value: string | null;
  onChange: (key: string) => void;
  disabled?: boolean;
  /** Accessible name for the group, e.g. the "Why are you cancelling?" prompt. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Single-select list of radio rows. Shared by the cancel-listing and
 * reject/cancel-order modals so both read as a standard "pick one, then
 * confirm" dialog rather than a stack of flat panels.
 *
 * Visuals live in global.scss under `.hp-choice` (see the note there on why
 * they can't be Tailwind utilities). Semantics are a real radiogroup: buttons
 * carry role="radio" + aria-checked, and only the selected row (or the first,
 * when nothing is chosen) is in the tab order, which is the standard
 * roving-tabindex behavior screen-reader users expect from a radio group.
 */
export const RadioOptionList: React.FC<RadioOptionListProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
  className,
}) => {
  const focusIndex = Math.max(
    0,
    options.findIndex((o) => o.key === value),
  );

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`space-y-2 ${className ?? ''}`}
    >
      {options.map((option, i) => {
        const isSelected = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={i === focusIndex ? 0 : -1}
            onClick={() => onChange(option.key)}
            disabled={disabled}
            className={`hp-choice ${isSelected ? 'hp-choice--selected' : ''}`}
          >
            <span className="hp-choice__radio" aria-hidden="true" />
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};
